import React from 'react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function Button({ className, variant = 'default', size = 'default', ...props }: any) {
  const variants: any = {
    default: 'bg-[#D97706] text-white hover:bg-[#B45309]',
    outline: 'border border-orange-200 bg-transparent hover:bg-orange-50 text-[#451A03]',
    ghost: 'hover:bg-orange-50 text-[#451A03]',
    icon: 'p-2'
  };
  const sizes: any = {
    default: 'px-4 py-2 rounded-lg',
    sm: 'px-3 py-1.5 rounded-md text-sm',
    lg: 'px-6 py-3 rounded-xl text-lg',
    icon: 'h-10 w-10 flex items-center justify-center'
  };
  return (
    <button 
      className={cn(
        'inline-flex items-center justify-center font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    />
  );
}

export function Badge({ className, variant = 'default', ...props }: any) {
  const variants: any = {
    default: 'bg-[#D97706] text-white border-transparent',
    outline: 'border border-orange-200 text-[#451A03]'
  };
  return (
    <div className={cn('inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors', variants[variant], className)} {...props} />
  );
}

export function Card({ className, ...props }: any) {
  return <div className={cn('rounded-2xl border border-orange-100 bg-white text-card-foreground shadow-sm', className)} {...props} />;
}

export function CardHeader({ className, ...props }: any) {
  return <div className={cn('flex flex-col space-y-1.5 p-6', className)} {...props} />;
}

export function CardTitle({ className, ...props }: any) {
  return <h3 className={cn('text-2xl font-semibold leading-none tracking-tight', className)} {...props} />;
}

export function CardContent({ className, ...props }: any) {
  return <div className={cn('p-6 pt-0', className)} {...props} />;
}

export function CardFooter({ className, ...props }: any) {
  return <div className={cn('flex items-center p-6 pt-0', className)} {...props} />;
}

export function Input({ className, ...props }: any) {
  return (
    <input
      className={cn(
        'flex h-12 w-full rounded-xl border border-orange-100 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D97706] disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      {...props}
    />
  );
}

export function Label({ className, ...props }: any) {
  return <label className={cn('text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70', className)} {...props} />;
}

export function Checkbox({ className, onCheckedChange, ...props }: any) {
  return (
    <input
      type="checkbox"
      onChange={(e) => onCheckedChange?.(e.target.checked)}
      className={cn('h-4 w-4 rounded border-orange-300 text-[#D97706] focus:ring-[#D97706]', className)}
      {...props}
    />
  );
}

export function RadioGroup({ onValueChange, children, className }: any) {
  return <div className={cn('grid gap-2', className)} onChange={(e: any) => onValueChange?.(e.target.value)}>{children}</div>;
}

export function RadioGroupItem({ value, id, ...props }: any) {
  return <input type="radio" name="radio-group" value={value} id={id} className="h-4 w-4 border-orange-300 text-[#D97706] focus:ring-[#D97706]" {...props} />;
}

export function Dialog({ open, onOpenChange, children }: any) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
        <button onClick={() => onOpenChange?.(false)} className="absolute top-4 right-4 p-2 rounded-full hover:bg-orange-50 text-gray-400 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
        {children}
      </div>
    </div>
  );
}

export function DialogContent({ children, className }: any) {
  return <div className={cn('p-6', className)}>{children}</div>;
}

export function DialogHeader({ className, ...props }: any) {
  return <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left mb-4', className)} {...props} />;
}

export function DialogTitle({ className, ...props }: any) {
  return <h2 className={cn('text-lg font-semibold leading-none tracking-tight', className)} {...props} />;
}
